<template>
    <div class="general-modal">
        <BaseModal :padding="isMobileOrTablet ? MODAL_PADDING_MOBILE : MODAL_PADDING_DESKTOP"
                   maxWidth="450px"
                   maxHeight="550px"
                   useContentFullHeight
                   :isClosable="false">
            <template #header/>
            <template #content>
                <div class="general-modal__content general-modal__content--justify-center">
                    <div class="content-wrapper">
                        <div v-for="(item, index) in data"
                             :key="item.title"
                             class="content">
                            <div v-if="index === step"
                                 class="slide-in">
                                <div>
                                    <img :src="asset_cdn(item.image)"
                                         class="intro-image"
                                         :alt="item.title"
                                         :title="item.title">
                                </div>
                                <div class="general-modal__title">
                                    {{ item.title }}
                                </div>
                                <div v-if="item.subtitle"
                                     class="general-modal__subtitle">
                                    {{ item.subtitle }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="general-modal__actions">
                    <div class="slider-controls">
                        <div v-for="index in 3"
                             :key="index"
                             class="slider-dot"
                             :class="{ active: index - 1 === step }"
                             @click="goToSlide(index - 1)" />
                    </div>
                    <span class="general-modal__action general-modal__action--confirm">
                        <BaseButton v-focus
                                    :variant="isLastStep ? ButtonVariantEnum.PRIMARY : ButtonVariantEnum.TERTIARY"
                                    @click="handleContinueButtonClick"
                                    @keyup.enter="handleContinueButtonClick">
                            <Translate v-if="isLastStep"
                                       translationKey="quests-dashboard.startTheJourney"
                                       fallback="Start The Journey!" />
                            <Translate v-else
                                       translationKey="quests-dashboard.continue"
                                       fallback="Continue" />
                        </BaseButton>
                    </span>
                </div>
            </template>
        </BaseModal>
    </div>
</template>

<script setup lang="ts">
    import { computed, ref } from 'vue';
    import BaseModal from '@/js/components/BaseModal.vue';
    import { useModalActive } from '@/js/composables/useModalActive';
    import { useDeviceSize } from '@/js/composables/useDeviceSize';
    import BaseButton from '@/js/components/BaseButton.vue';
    import { ButtonVariantEnum } from '@/js/types/components/ButtonVariantEnum';
    import { asset_cdn } from '@/js/helpers/helpers';
    import { getTrans } from '@/js/helpers/getTrans';
    import { MODAL_PADDING_DESKTOP, MODAL_PADDING_MOBILE } from '@/js/quests/global/constants/modal';

    const emit = defineEmits<{(e: 'close'): void; }>();

    const { isMobileOrTablet } = useDeviceSize();

    const step = ref(0);

    const data = [
        {
            title: getTrans('quests-dashboard.exploreMissions', 'Explore Missions'),
            subtitle: getTrans('quests-dashboard.improveWeb3Skills', 'Improve your Web3 skills by completing Missions the fun way. Anytime. Anywhere.'),
            image: 'assets/missions/introduction-1.svg',
        },
        {
            title: getTrans('quests-dashboard.keepTheStreakUp', 'Keep The Streak Up'),
            subtitle: getTrans('quests-dashboard.practicingRoundsDaily', 'By practicing Rounds daily, build strong habits and get the maximum Bits.'),
            image: 'assets/missions/introduction-2.svg',
        },
        {
            title: getTrans('quests-dashboard.gainRealRewards', 'Gain Real Rewards'),
            subtitle: getTrans('quests-dashboard.collectBitsAndBoost', 'Collect Bits, boost your Degree and gain actual rewards!'),
            image: 'assets/missions/introduction-3.svg',
        },
    ];

    const isLastStep = computed(() => step.value === data.length - 1);

    const goToSlide = (index: number) => {
        step.value = index;
    };

    const handleContinueButtonClick = () => {
        if (isLastStep.value) {
            emit('close');
        } else {
            step.value += 1;
        }
    };

    useModalActive();
</script>

<style scoped lang="scss">
@use "@/sass/design-system/variables" as variables;
@use "@/sass/partials/_variables" as legacyVariables;
@use "@/js/quests/dashboard/sass/mixins/_modal" as modalMixins;

.general-modal {
    @include modalMixins.modal;

    &__actions {
        flex-direction: column;
        gap: 32px;
    }
}

.content-wrapper {
    .content {
        .slide-in {
            min-height: 325px;
        }
    }

    .intro-image {
        width: 100%;
        max-height: 210px;
        margin-bottom: 16px;
    }
}

.slider-controls {
    display: flex;
    align-items: center;
    margin-right: auto;
    margin-left: auto;
    justify-items: center;

    .slider-dot {
        width: 12px;
        height: 12px;
        border: 1px solid variables.$primary500;
        border-radius: 15px;
        margin: 0 4px;
        background-color: legacyVariables.$dark-purple;
        cursor: pointer;
        transition: width 0.2s ease-in-out;

        &.active,
        &:hover {
            width: 25px;
        }
    }
}

.slide-in {
    animation: slide-in 0.5s forwards;
}
</style>
